import { useCallback, useEffect, useMemo, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataStateRow } from "@/components/ui/data-state";
import { RefreshCcw, Search } from "lucide-react";

const ACTION_LABELS = {
  create:       "Tạo mới",
  update:       "Cập nhật",
  delete:       "Xoá",
  approve:      "Phê duyệt",
  reject:       "Từ chối",
  grant:        "Cấp quyền",
  revoke:       "Thu hồi quyền",
  expire:       "Hết hạn tự động",
  fulfill:      "Cấp phát",
  cancel:       "Huỷ yêu cầu",
  download:     "Tải tài liệu",
  view:         "Xem tài liệu",
  status_change: "Đổi trạng thái",
};

const RESOURCE_LABELS = {
  legal_entity:   "Pháp nhân",
  legal_doc:      "Tài liệu",
  legal_doc_type: "Loại giấy tờ",
  legal_request:  "Yêu cầu",
  legal_grant:    "Quyền truy cập",
};

const DEFAULT_FILTERS = { resource_type: "", resource_id: "", limit: "100" };

export default function LegalVaultAudit() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (applied.resource_type) p.set("resource_type", applied.resource_type);
    if (applied.resource_id) p.set("resource_id", applied.resource_id);
    if (applied.limit) p.set("limit", applied.limit);
    return p.toString();
  }, [applied]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/legal/audit${query ? `?${query}` : ""}`);
      setRows(r.data || []);
      setActive(r.data?.[0] || null);
    } catch (e) {
      setError(parseApiError(e, "Không tải được audit log pháp lý").message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const submit = (e) => {
    e.preventDefault();
    setApplied(filters);
  };

  return (
    <div data-testid="page-legal-audit">
      <PageHeader
        kicker="Admin • Audit pháp lý"
        title="Lịch sử hoạt động kho hồ sơ"
        breadcrumb={["Pháp lý", "Audit Trail"]}
        actions={
          <Button type="button" variant="outline" onClick={load} className="rounded-sm">
            <RefreshCcw className="w-4 h-4 mr-1.5" />Tải lại
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        {/* Filters */}
        <form onSubmit={submit}
          className="bg-white border border-stone-200 rounded-sm p-3 flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-stone-500">Loại đối tượng</label>
            <select
              className="border border-stone-200 rounded px-2 py-1.5 text-sm"
              value={filters.resource_type}
              onChange={(e) => setFilters((f) => ({ ...f, resource_type: e.target.value }))}
            >
              <option value="">Tất cả</option>
              {Object.entries(RESOURCE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-stone-500">ID đối tượng</label>
            <Input
              value={filters.resource_id}
              onChange={(e) => setFilters((f) => ({ ...f, resource_id: e.target.value }))}
              placeholder="UUID…"
              className="w-64"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-stone-500">Giới hạn</label>
            <select
              className="border border-stone-200 rounded px-2 py-1.5 text-sm"
              value={filters.limit}
              onChange={(e) => setFilters((f) => ({ ...f, limit: e.target.value }))}
            >
              {["50", "100", "200", "500"].map((n) => <option key={n} value={n}>{n} dòng</option>)}
            </select>
          </div>
          <Button type="submit" variant="outline" className="self-end">
            <Search className="w-4 h-4 mr-1.5" />Lọc
          </Button>
        </form>

        {/* Split view */}
        <div className="flex gap-4 min-h-[400px]">
          {/* List */}
          <div className="flex-1 bg-white border border-stone-200 rounded-sm overflow-auto">
            {loading && <DataStateRow loading />}
            {error && <div className="p-4 text-red-600 text-sm">{error}</div>}
            {!loading && !error && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 text-stone-500 text-xs bg-stone-50 sticky top-0">
                    <th className="px-3 py-2 text-left">Thời gian</th>
                    <th className="px-3 py-2 text-left">Hành động</th>
                    <th className="px-3 py-2 text-left">Đối tượng</th>
                    <th className="px-3 py-2 text-left">Người thực hiện</th>
                    <th className="px-3 py-2 text-left">Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-stone-400">Không có log nào</td></tr>
                  )}
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setActive(row)}
                      className={`border-b border-stone-50 cursor-pointer transition-colors ${active?.id === row.id ? "bg-stone-900 text-white" : "hover:bg-stone-50"}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        {new Date(row.created_at).toLocaleString("vi-VN")}
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-medium">{ACTION_LABELS[row.action] || row.action}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{RESOURCE_LABELS[row.resource_type] || row.resource_type}</div>
                        <div className="font-mono text-xs opacity-60">{row.resource_id.slice(0, 8)}…</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{row.actor_role}</td>
                      <td className="px-3 py-2 text-xs max-w-[200px] truncate">{row.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail panel */}
          {active && (
            <div className="w-80 flex-shrink-0 bg-white border border-stone-200 rounded-sm p-4 text-sm space-y-3 overflow-auto">
              <h3 className="font-semibold text-stone-800">{ACTION_LABELS[active.action] || active.action}</h3>
              <dl className="space-y-1.5 text-xs">
                <div><dt className="text-stone-500">Đối tượng</dt><dd className="font-mono break-all">{active.resource_type} / {active.resource_id}</dd></div>
                <div><dt className="text-stone-500">Vai trò</dt><dd>{active.actor_role}</dd></div>
                <div><dt className="text-stone-500">Thời gian</dt><dd>{new Date(active.created_at).toLocaleString("vi-VN")}</dd></div>
                <div><dt className="text-stone-500">Mô tả</dt><dd>{active.summary}</dd></div>
              </dl>
              {active.after_json && (
                <div>
                  <p className="text-xs font-semibold text-stone-500 mb-1">Sau thay đổi</p>
                  <pre className="bg-stone-50 rounded p-2 text-xs overflow-auto max-h-48 whitespace-pre-wrap">
                    {typeof active.after_json === "string"
                      ? active.after_json
                      : JSON.stringify(active.after_json, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
